/**
 * Curated Playlists Component
 * Displays admin-curated playlists that users can browse
 * Playlists are managed by admin in the AdminPanel
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

// Legacy playlist definitions - kept for backward compatibility during transition
// These will be replaced by database playlists. Colors aligned with DJ LYO brand (cyan + grays).
const PLAYLIST_DEFINITIONS = [
  { id: 'fresh', name: 'Fresh Releases', subtitle: 'Últimos 7 días', icon: '', color: '#00D9FF', gradient: 'from-[#00D9FF] to-[#00B8D9]', filter: () => false },
  { id: 'trending', name: 'Trending Now', subtitle: 'Más descargados', icon: '', color: '#00D9FF', gradient: 'from-[#00D9FF] to-[#0088FF]', filter: () => false },
  { id: 'club_hits', name: 'Club Hits', subtitle: 'Peak time bangers', icon: '', color: '#00D9FF', gradient: 'from-[#00D9FF] to-[#1A1A1A]', filter: () => false },
  { id: 'reggaeton_hits', name: 'Reggaetón Hits', subtitle: 'Lo mejor del género', icon: '', color: '#00D9FF', gradient: 'from-[#00D9FF] to-[#0088FF]', filter: () => false },
  { id: 'dembow', name: 'Dembow', subtitle: 'Dominican heat', icon: '', color: '#B3B3B3', gradient: 'from-[#B3B3B3] to-[#333]', filter: () => false },
  { id: 'wedding', name: 'Wedding Essentials', subtitle: 'Clásicos para bodas', icon: '', color: '#B3B3B3', gradient: 'from-[#B3B3B3] to-[#333]', filter: () => false },
];

const CuratedPlaylists = ({ onSelectPlaylist, selectedPlaylist }) => {
  const [playlists, setPlaylists] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(true);

  // Fetch curated AND manual playlists from database
  useEffect(() => {
    const fetchPlaylists = async () => {
      try {
        const [curatedRes, manualRes] = await Promise.all([
          api.get('/playlists/curated', { withCredentials: true }),
          api.get('/playlists/manual', { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        const curated = Array.isArray(curatedRes.data) ? curatedRes.data : (curatedRes.data.playlists || []);
        const manual = Array.isArray(manualRes.data) ? manualRes.data : (manualRes.data.playlists || []);
        // Mark manual playlists so we use the correct endpoint
        const manualMarked = manual.map(p => ({ ...p, _type: 'manual' }));
        setPlaylists([...curated, ...manualMarked]);
      } catch (error) {
        console.error('Error fetching playlists:', error);
        setPlaylists(PLAYLIST_DEFINITIONS.map(p => ({
          ...p,
          track_count: 0
        })));
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlaylists();
  }, []);

  const handleSelectPlaylist = async (playlist) => {
    if (selectedPlaylist?.playlist_id === playlist.playlist_id) {
      // Deselect if already selected
      onSelectPlaylist(null);
      return;
    }
    
    try {
      // Use correct endpoint based on type
      const endpoint = playlist._type === 'manual' 
        ? `/playlists/manual/${playlist.playlist_id}`
        : `/playlists/curated/${playlist.playlist_id}`;
      const response = await api.get(endpoint, { withCredentials: true });
      onSelectPlaylist({
        ...response.data,
        gradient: getGradient(playlist.color)
      });
    } catch (error) {
      console.error('Error fetching playlist:', error);
      alert('Error al cargar playlist: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getGradient = (color) => {
    // Brand-strict: all gradients map to cyan or neutral variants
    const gradients = {
      '#FF4444': 'from-[#00D9FF] to-[#00B8D9]',
      '#00D9FF': 'from-[#00D9FF] to-[#0088FF]',
      '#FF00F5': 'from-[#00D9FF] to-[#1A1A1A]',
      '#00FF88': 'from-[#00D9FF] to-[#0088FF]',
      '#FFB800': 'from-[#B3B3B3] to-[#333]',
      '#FFD700': 'from-[#B3B3B3] to-[#333]',
    };
    return gradients[color] || 'from-[#00D9FF] to-[#0088FF]';
  };

  // Coerce any legacy backend playlist color into the brand palette
  const brandColor = (c) => {
    const map = {
      '#FF4444': '#00D9FF',
      '#FF00F5': '#00D9FF',
      '#00FF88': '#00D9FF',
      '#FFB800': '#B3B3B3',
      '#FFD700': '#B3B3B3',
      '#00D9FF': '#00D9FF',
      '#00D4FF': '#00D4FF',
    };
    return map[c] || '#00D9FF';
  };

  if (loading) {
    return (
      <div className="mb-4 px-3 py-2">
        <div className="text-[#555] text-xs">Cargando playlists...</div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Header */}
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[#888]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M9 19V6l12-3v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/></svg>
          <span className="text-xs font-bold text-[#888] uppercase tracking-wider">
            Playlists
          </span>
        </div>
        <motion.svg 
          animate={{ rotate: isExpanded ? 0 : -90 }}
          className="w-4 h-4 text-[#555]"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
        </motion.svg>
      </motion.button>

      {/* Playlist Grid */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 gap-1.5 px-2 pt-2">
              {playlists.map((playlist, index) => (
                <motion.button
                  key={playlist.playlist_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleSelectPlaylist(playlist)}
                  className={`flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                    selectedPlaylist?.playlist_id === playlist.playlist_id
                      ? 'bg-gradient-to-r ' + getGradient(playlist.color) + ' shadow-lg'
                      : 'bg-[#141414] hover:bg-[#1A1A1A] border border-[#1A1A1A] hover:border-[#262626]'
                  }`}
                >
                  <div 
                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg shrink-0 ${
                      selectedPlaylist?.playlist_id === playlist.playlist_id
                        ? 'bg-white/20'
                        : ''
                    }`}
                    style={selectedPlaylist?.playlist_id !== playlist.playlist_id ? { backgroundColor: `${brandColor(playlist.color)}20` } : {}}
                  >
                    {playlist.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${
                      selectedPlaylist?.playlist_id === playlist.playlist_id ? 'text-white' : 'text-white'
                    }`}>
                      {playlist.name}
                    </div>
                    <div className={`text-xs truncate ${
                      selectedPlaylist?.playlist_id === playlist.playlist_id ? 'text-white/70' : 'text-[#666]'
                    }`}>
                      {playlist.description || playlist.subtitle}
                    </div>
                  </div>
                  <div className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${
                    selectedPlaylist?.playlist_id === playlist.playlist_id
                      ? 'bg-white/20 text-white'
                      : 'text-[#555]'
                  }`}
                  style={selectedPlaylist?.playlist_id !== playlist.playlist_id ? { backgroundColor: `${brandColor(playlist.color)}15`, color: brandColor(playlist.color) } : {}}>
                    {playlist.track_count || 0}
                  </div>
                </motion.button>
              ))}
              
              {playlists.length === 0 && (
                <div className="text-center py-4 text-[#555] text-xs">
                  No hay playlists curadas disponibles
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { PLAYLIST_DEFINITIONS };
export default CuratedPlaylists;
